import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { useAppSelector } from '../../../../../redux/hooks';
import { lddGlobalSessionInfo } from '../../../../navigationTypes';
import StateContext from '../../../context/State';
import PortalService from '../../../services/portal.service';
import { getCustomFieldSet } from '../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/customFieldSetFuncs';
import type { ClientConnectCustomFieldSet } from '../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/types';
import { NewCustomFieldSetModal } from '../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/newCustomFieldSet';
import { runInnerFunction } from '../../../../../compatManager';
import { buildMergedPortalCustomFields, distinctIntakeFormNamesFromPortalRows } from './intakeTemplateMerge';
import { getDataReader } from '../../../../../livedataFuncs';
import { lddStateObject } from '../../../../../redux/reducer';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

function intakeTemplateSelectionChanged(prevNames: string[], nextNames: string[]): boolean {
    const a = [...prevNames].sort().join('\u0000');
    const b = [...nextNames].sort().join('\u0000');
    return a !== b;
}

export type AddIntakeTemplatesModalProps = {
    show: boolean;
    onHide: () => void;
    onSaved?: () => void;
};

const AddIntakeTemplatesModal = (props: AddIntakeTemplatesModalProps) => {
    const { state, bumpDashboardFieldsPoll } = useContext(StateContext);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((s) => s.SessionInfo);
    const livedataRootContainer = useAppSelector((s: lddStateObject) => s.currentNavigation.livedataRootContainer);
    const portalId = state.portalId;

    const portalService = new PortalService(state);
    const portalServiceRef = useRef(portalService);
    portalServiceRef.current = portalService;

    const [firmFieldSets, setFirmFieldSets] = useState<ClientConnectCustomFieldSet[]>([]);
    const [selectedNames, setSelectedNames] = useState<string[]>([]);
    const [lockedIncludedNames, setLockedIncludedNames] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [previewFieldSet, setPreviewFieldSet] = useState<ClientConnectCustomFieldSet | null>(null);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        if (!props.show) {
            setLockedIncludedNames([]);
            return;
        }
        if (!portalId) {
            setLockedIncludedNames([]);
            return;
        }
        let cancelled = false;
        const svc = portalServiceRef.current;
        void (async () => {
            try {
                const [sets, rows, xmlSelected] = await Promise.all([
                    getCustomFieldSet(sessionInfo.sid),
                    svc.getClientPortalCustomFields(portalId).catch(() => []),
                    svc.getSelectedCustomFieldSetsFromXML(portalId).catch(() => [])
                ]);
                if (cancelled) return;
                setFirmFieldSets(Array.isArray(sets) ? sets : []);
                const rowList = Array.isArray(rows) ? rows : [];
                const fromDb = distinctIntakeFormNamesFromPortalRows(rowList);
                const fromXml = Array.isArray(xmlSelected) ? xmlSelected : [];
                const initial = fromDb.length > 0 ? fromDb : [...fromXml];
                setSelectedNames(initial);
                setLockedIncludedNames([...initial]);
            } catch {
                if (!cancelled) {
                    setFirmFieldSets([]);
                    setSelectedNames([]);
                    setLockedIncludedNames([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [props.show, portalId, sessionInfo.sid]);

    const handleToggle = (fieldSetName: string, checked: boolean) => {
        if (lockedIncludedNames.includes(fieldSetName)) {
            return;
        }
        setSelectedNames((prev) => {
            if (checked) {
                return prev.includes(fieldSetName) ? prev : [...prev, fieldSetName];
            }
            return prev.filter((n) => n !== fieldSetName);
        });
    };

    const handleSave = async () => {
        if (!portalId || saving) return;
        setSaving(true);
        const svc = portalServiceRef.current;
        try {
            const currentRows = await svc.getClientPortalCustomFields(portalId);
            const currentList = Array.isArray(currentRows) ? currentRows : [];
            const prevList = distinctIntakeFormNamesFromPortalRows(currentList);
            const selectionChanged = intakeTemplateSelectionChanged(prevList, selectedNames);

            const merged = buildMergedPortalCustomFields(
                prevList,
                selectedNames,
                firmFieldSets,
                currentList
            );

            await svc.setClientPortalCustomFields(portalId, merged, 'afterInit');

            await runInnerFunction('ClientPortalUpdateAndSaveFile', {
                clientConnectId: portalId,
                customFieldSets: selectedNames
            });

            if (selectionChanged && state.appId) {
                try {
                    const portals = await svc.getPortalByAppId(state.appId);
                    if (portals && portals.length > 0 && portals[0].CreatedDateTime) {
                        const created = new Date(portals[0].CreatedDateTime as string);
                        if (!Number.isNaN(created.getTime()) && Date.now() - created.getTime() > THIRTY_MINUTES_MS) {
                            const dataRoot = livedataRootContainer?.dataRoot;
                            if (dataRoot) {
                                getDataReader(dataRoot).setDashboardFieldsDirty('addIntakeFields');
                                bumpDashboardFieldsPoll();
                            }
                        }
                    }
                } catch (notifyErr) {
                    console.error('Intake save: could not set dashboard notify flag', notifyErr);
                }
            }

            alert('Intake templates saved.');
            props.onSaved?.();
            props.onHide();
        } catch (e) {
            console.error('Save intake templates failed', e);
            alert('Unable to save intake templates. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openPreview = (fs: ClientConnectCustomFieldSet) => {
        setPreviewFieldSet(fs);
        setShowPreview(true);
    };

    const closePreview = () => {
        setPreviewFieldSet(null);
        setShowPreview(false);
    };

    return (
        <>
            <Modal
                show={props.show}
                onHide={props.onHide}
                aria-labelledby="intake-templates-modal-title"
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="intake-templates-modal-title">Add Intake Forms from Templates</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h3 className="h6 fw-bold">Include Intake Templates</h3>
                    <Table hover bordered className="mb-0">
                        <thead>
                            <tr>
                                <th>Template Name</th>
                                <th className="text-end">Include</th>
                            </tr>
                        </thead>
                        <tbody>
                            {firmFieldSets.length === 0 ? (
                                <tr>
                                    <td colSpan={2}>
                                        <Alert className="mb-0">
                                            No intake templates are defined in firm settings.
                                        </Alert>
                                    </td>
                                </tr>
                            ) : (
                                firmFieldSets.map((fs, index) => {
                                    const isLockedIncluded = lockedIncludedNames.includes(fs.fieldSetName);
                                    return (
                                    <tr key={`intake-template-${fs.fieldSetName}-${index}`}>
                                        <td
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openPreview(fs)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openPreview(fs);
                                                }
                                            }}
                                        >
                                            {fs.fieldSetName}
                                        </td>
                                        <td
                                            className="text-end"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                aria-label={
                                                    isLockedIncluded
                                                        ? `${fs.fieldSetName} already included (read only)`
                                                        : `Include ${fs.fieldSetName}`
                                                }
                                                checked={selectedNames.includes(fs.fieldSetName)}
                                                disabled={isLockedIncluded}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleToggle(fs.fieldSetName, e.target.checked);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={props.onHide} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => void handleSave()} disabled={saving || firmFieldSets.length === 0}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>

            <NewCustomFieldSetModal
                show={showPreview}
                onHide={closePreview}
                editFieldSet={previewFieldSet}
                fromSettings={false}
            />
        </>
    );
};

export default AddIntakeTemplatesModal;
